import { TypedDocumentNode, gql, useApolloClient } from '@apollo/client';
import {
  atLeastOne,
  convertMeasure,
  isMeasureWithValue,
  mustBeEqual,
  mustBeGreaterOrEqual,
  mustBeLessOrEqual,
} from '@e2m-customer-portal/shared';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-use';
import { NullableMeasure, QueryPowerLevelsArgs } from '../../generated/graphql';
import { NULLABLE_MEASURE_FIELDS } from '../../queries';

// Validation rules: PROD_15 PROD_16 PLL_01 PLL_02 PLL_03 PLL_04 PLL_05 PLL_06
export interface PowerLevelsResponse {
  powerLevels: NullableMeasure[];
}

export const powerLevelsQuery: TypedDocumentNode<PowerLevelsResponse, QueryPowerLevelsArgs> = gql`
  query PowerLevels($deviceId: String!) {
    powerLevels(deviceId: $deviceId) {
      ...NullableMeasureFields
    }
  }
  ${NULLABLE_MEASURE_FIELDS}
`;

export function usePowerLevels(deviceId?: string): ReadonlyArray<NullableMeasure> | undefined {
  const client = useApolloClient();

  const { value } = useAsync(async () => {
    if (!deviceId) return;

    const { data } = await client.query({
      query: powerLevelsQuery,
      variables: {
        deviceId,
      },
    });

    return data?.powerLevels;
  }, [deviceId]);

  return value;
}

export function usePowerLevelsRules(powerLevels: readonly NullableMeasure[], unit: string, production?: number) {
  const [t] = useTranslation('validation');
  // rules for single power level (TUG)
  if (powerLevels?.length === 1 && isMeasureWithValue(powerLevels[0])) {
    // different rule for FCR (PRL) which is a band and must be consider the production value
    if (production !== undefined) {
      return atLeastOne(
        [mustBeLessOrEqual(production - convertMeasure(powerLevels[0], unit).value), mustBeEqual(0)],
        t('COMMON_FCR_BAND_VIOLATES_DISCRETE_POWER_LEVEL_UPPER_LIMIT', {
          value: { value: Math.abs(production - convertMeasure(powerLevels[0], unit).value), unit: 'kW' },
          unit,
        }),
      );
    } else {
      return atLeastOne(
        [mustBeGreaterOrEqual(convertMeasure(powerLevels[0], unit).value), mustBeEqual(0)],
        t('COMMON_MUST_BE_GREATER_THAN_DISCRETE_POWER_LEVEL_UPPER_LIMIT', {
          value: powerLevels[0],
          unit,
        }),
      );
    }
    // rules for multiple power levels
  } else if (powerLevels && powerLevels.length > 1) {
    const values = powerLevels.map((powerlevel) => `${convertMeasure(powerlevel, unit).value} ${unit}`);

    const validations = powerLevels?.map((powerlevel) => {
      return mustBeEqual(Number(convertMeasure(powerlevel, unit).value));
    });
    return atLeastOne(
      validations,
      t('COMMON_VIOLATES_POWER_LEVEL', {
        value: values,
      }),
    );
  }
}
